import React from "react";

export interface Tab {
  id: string;
  label: string;
  disabled?: boolean;
}

interface TabNavigationProps {
  tabs: Tab[];
  activeTab: string;
  onTabChange: (tabId: string) => void;
  className?: string;
}

/**
 * TabNavigation - Reusable tab component following SOLID principles
 * Single Responsibility: Manages tab navigation state and rendering
 * Open/Closed: Extensible through props without modification
 * Interface Segregation: Clean, focused interface
 */
export const TabNavigation: React.FC<TabNavigationProps> = ({
  tabs,
  activeTab,
  onTabChange,
  className = "",
}) => {
  const handleTabClick = (tabId: string, disabled?: boolean) => {
    if (!disabled) {
      onTabChange(tabId);
    }
  };

  const handleKeyDown = (
    event: React.KeyboardEvent,
    tabId: string,
    disabled?: boolean
  ) => {
    if ((event.key === "Enter" || event.key === " ") && !disabled) {
      event.preventDefault();
      onTabChange(tabId);
    }
  };

  return (
    <nav className={`tab-navigation ${className}`} role="tablist">
      <ul className="tab-list">
        {tabs.map((tab) => (
          <li key={tab.id} className="tab-item" role="none">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={`tabpanel-${tab.id}`}
              className={`tab-button ${activeTab === tab.id ? "active" : ""} ${
                tab.disabled ? "disabled" : ""
              }`}
              onClick={() => handleTabClick(tab.id, tab.disabled)}
              onKeyDown={(e) => handleKeyDown(e, tab.id, tab.disabled)}
              disabled={tab.disabled}
              tabIndex={activeTab === tab.id ? 0 : -1}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>
    </nav>
  );
};
